// 待办事项存储逻辑（Web 应用版本）
import type { Todo } from '@ext-bookmark/shared/types';
import { getAppData, saveAppData } from '../../lib/storage';
import { getCurrentUser } from '../auth/authAdapter';
import { enqueueTodoOp } from '../sync/todoOfflineQueue';
import { calculateRecurrenceDates } from './recurrence';
import { saveTodoToSupabase } from './supabaseStorage';
import { getUserGroups } from '../group/groupService';
import { markTodoUpdatedByUs } from './lastTodoUpdateByUs';

const generateId = (): string => {
  return `todo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * 获取待办的 base id。重复事项实例由 getTodosInDateRangeFromList 展开时设置 baseId；
 * 非重复事项或 base 待办无 baseId，直接返回 id。
 */
export function getBaseTodoId(todo: { id: string; baseId?: string }): string {
  return todo.baseId ?? todo.id;
}

/** 判断两个时间戳是否表示同一日历日（本地时区），供外部使用 */
export function isSameCalendarDay(ts1: number, ts2: number): boolean {
  const d1 = new Date(ts1);
  const d2 = new Date(ts2);
  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
}

/**
 * 获取所有待办事项
 * 注意：此函数仅从本地存储读取数据
 * Supabase 数据通过定时任务自动拉取并更新到本地存储，因此查询操作不需要调用 Supabase API
 */
export async function getTodos(): Promise<Todo[]> {
  const data = await getAppData();
  return data.todos || [];
}

/**
 * 保存待办事项。已登录时先写 Supabase，成功则更新本地；失败则入离线队列。
 */
export async function saveTodo(todo: Todo): Promise<Todo> {
  const data = await getAppData();
  const todos = data.todos || [];
  const currentUser = await getCurrentUser();
  const currentUserId = currentUser?.id;

  const existingIndex = todos.findIndex((t) => t.id === todo.id);
  let savedTodo: Todo;

  if (existingIndex >= 0) {
    savedTodo = {
      ...todo,
      updatedAt: Date.now(),
      createdBy: todo.createdBy || todos[existingIndex].createdBy || currentUserId,
    };
    todos[existingIndex] = savedTodo;
  } else {
    savedTodo = {
      ...todo,
      id: todo.id || generateId(),
      createdAt: todo.createdAt || Date.now(),
      updatedAt: Date.now(),
      createdBy: todo.createdBy || currentUserId,
    };
    todos.push(savedTodo);
  }

  // 已登录：先写 Supabase，失败则入队
  if (currentUser) {
    try {
      const { error } = await saveTodoToSupabase(savedTodo);
      if (error) throw new Error(error.message);
    } catch (e) {
      enqueueTodoOp('SAVE_TODO', savedTodo);
      throw e;
    }
  }

  // 更新本地存储（skipAutoSync：待办已直接写 Supabase，不触发 syncQueue）
  await saveAppData({ ...data, todos }, true);
  return savedTodo;
}

/**
 * 删除待办事项。已登录时先删 Supabase，失败则入队。
 */
export async function deleteTodo(todoId: string): Promise<void> {
  const data = await getAppData();
  const todos = (data.todos || []).filter((t) => t.id !== todoId);
  const currentUser = await getCurrentUser();

  if (currentUser) {
    try {
      const { deleteTodoFromSupabase } = await import('./supabaseStorage');
      const { error } = await deleteTodoFromSupabase(todoId);
      if (error) throw new Error(error.message);
    } catch (e) {
      enqueueTodoOp('DELETE_TODO', { todoId });
      throw e;
    }
  }

  await saveAppData({ ...data, todos }, true);
}

/**
 * 批量删除待办事项。已登录时先删 Supabase，失败则入队。
 */
export async function deleteTodos(todoIds: string[]): Promise<void> {
  const data = await getAppData();
  const todos = (data.todos || []).filter((t) => !todoIds.includes(t.id));
  const currentUser = await getCurrentUser();

  if (currentUser) {
    try {
      const { deleteTodosFromSupabase } = await import('./supabaseStorage');
      const { error } = await deleteTodosFromSupabase(todoIds);
      if (error) throw new Error(error.message);
    } catch (e) {
      enqueueTodoOp('DELETE_TODOS', { todoIds });
      throw e;
    }
  }

  await saveAppData({ ...data, todos }, true);
}

export interface ToggleTodoCompleteOptions {
  /** 已登录时传入当前数据源（如 React Query 的 todosData），避免依赖未同步的 localStorage */
  currentTodos?: Todo[];
  /** 优先使用：在切换时实时获取最新数据，避免闭包中的 currentTodos 滞后导致基于错误状态切换 */
  getCurrentTodos?: () => Todo[] | undefined;
  /** 重复事项：传入当前实例的完成状态（用户看到的 UI 状态），避免 completedDates 推断因缓存滞后或时间戳不一致出错 */
  currentInstanceCompleted?: boolean;
}

/**
 * 切换待办事项完成状态（同时更新本地存储和 Supabase）
 * @param todoId 待办事项ID
 * @param instanceDate 可选：重复事项实例的日期时间戳（只包含日期部分，时间设为00:00:00）
 * @param options 可选：currentTodos 存在时优先从中查找待办，否则从 localStorage 读取
 */
export async function toggleTodoComplete(
  todoId: string,
  instanceDate?: number,
  options?: ToggleTodoCompleteOptions
): Promise<Todo | null> {
  const data = await getAppData();
  let todos = options?.getCurrentTodos?.() ?? options?.currentTodos ?? data.todos ?? [];
  let todo = todos.find((t) => t.id === todoId);
  // 新增待办可能尚未进入 React Query 缓存（如 TodoForm setQueryData 时 cache 为空），
  // 但已写入 localStorage，日历网格可能从 localStorage 派生显示。若缓存中未找到则回退到 localStorage。
  if (!todo && (options?.getCurrentTodos || options?.currentTodos)) {
    const localTodos = data.todos ?? [];
    todo = localTodos.find((t) => t.id === todoId);
    if (todo) todos = localTodos;
  }
  if (!todo) {
    return null;
  }

  // 权限检查：如果任务有分配人，只有被分配的人可以完成
  if (todo.assignedTo && todo.assignedTo.length > 0) {
    const currentUser = await getCurrentUser();
    if (!currentUser || !todo.assignedTo.includes(currentUser.id)) {
      throw new Error('只有被分配的用户可以完成此任务');
    }
  }
  
  let updatedTodo: Todo;
  
  // 如果是重复事项且有传入实例日期，更新 completedDates
  if (todo.recurrence && instanceDate !== undefined) {
    // 规范化日期时间戳（只包含日期部分，时间设为00:00:00）
    const normalizedDate = new Date(instanceDate);
    normalizedDate.setHours(0, 0, 0, 0);
    const dateTimestamp = normalizedDate.getTime();
    
    // 获取或初始化 completedDates 数组
    const completedDates = todo.recurrence.completedDates || [];
    // 优先使用调用方传入的 UI 状态，避免 getCurrentTodos 缓存滞后或时间戳不一致导致误判
    const isCompleted = options?.currentInstanceCompleted !== undefined
      ? options.currentInstanceCompleted
      : completedDates.includes(dateTimestamp);
    
    // 更新 completedDates 数组（按同一日历日匹配，避免时区/时间戳格式不一致导致移除失败）
    const newCompletedDates = isCompleted
      ? completedDates.filter(d => !isSameCalendarDay(d, dateTimestamp))
      : [...completedDates.filter(d => !isSameCalendarDay(d, dateTimestamp)), dateTimestamp];
    
    updatedTodo = {
      ...todo,
      recurrence: {
        ...todo.recurrence,
        completedDates: newCompletedDates,
      },
      updatedAt: Date.now(),
    };
  } else {
    // 非重复事项：优先用 currentInstanceCompleted（点击前状态），避免 getCurrentTodos 返回已被乐观更新后的缓存导致 !todo.completed 计算错误
    const newCompleted = options?.currentInstanceCompleted !== undefined
      ? !options.currentInstanceCompleted
      : !todo.completed;
    updatedTodo = {
      ...todo,
      completed: newCompleted,
      completedAt: newCompleted ? Date.now() : undefined,
      updatedAt: Date.now(),
    };
  }
  
  const updatedTodos = todos.map((t) => (t.id === todoId ? updatedTodo : t));

  // 更新本地存储（skipAutoSync：待办直接写 Supabase，不触发 syncQueue）
  await saveAppData(
    {
      ...data,
      todos: updatedTodos,
    },
    true
  );

  // 已登录：先写 Supabase，失败则入队（不抛错，乐观更新，后台同步）
  const currentUser = await getCurrentUser();
  if (currentUser) {
    markTodoUpdatedByUs(updatedTodo.id);
    try {
      const { error } = await saveTodoToSupabase(updatedTodo, { skipExistingFetch: true });
      if (error) {
        enqueueTodoOp('TOGGLE_TODO', { todoId, instanceDate });
        console.warn('[Storage] 勾选状态同步到 Supabase 失败，已入离线队列:', error.message);
      }
    } catch (e) {
      enqueueTodoOp('TOGGLE_TODO', { todoId, instanceDate });
      console.warn('[Storage] 勾选状态同步到 Supabase 异常，已入离线队列:', e);
    }
  }

  return updatedTodo;
}

/**
 * 从给定待办列表中展开并筛选出日期范围内的待办（含重复规则）
 * 同步函数，供日历网格与右侧面板统一派生，保证数据一致
 */
export function getTodosInDateRangeFromList(
  todos: Todo[],
  startDate: Date,
  endDate: Date
): Todo[] {
  const result: Todo[] = [];

  for (const todo of todos) {
    // 无日期待办（startDate 为 0）不参与日历日期范围展开，仅出现在待办视图中
    if (todo.startDate === 0) continue;
    const todoStartDate = new Date(todo.startDate);
    const todoEndDate = todo.endDate ? new Date(todo.endDate) : undefined;

    if (todo.recurrence) {
      const dates = calculateRecurrenceDates(
        todo.recurrence,
        todoStartDate,
        { start: startDate, end: endDate },
        todoEndDate
      );
      for (const date of dates) {
        const normalizedDate = new Date(date);
        normalizedDate.setHours(0, 0, 0, 0);
        const dateTimestamp = normalizedDate.getTime();
        const completedDates = todo.recurrence.completedDates || [];
        const isCompleted = completedDates.some(d => isSameCalendarDay(d, dateTimestamp));
        result.push({
          ...todo,
          id: `${todo.id}_${date.getTime()}`,
          baseId: todo.id,
          startDate: date.getTime(),
          completed: isCompleted,
          completedAt: isCompleted ? dateTimestamp : undefined,
        });
      }
    } else {
      const isInRange = todoStartDate >= startDate && todoStartDate <= endDate;
      const isBeforeEndDate = !todoEndDate || todoStartDate <= todoEndDate;
      if (isInRange && isBeforeEndDate) {
        result.push(todo);
      }
    }
  }
  return result;
}

/**
 * 获取指定日期范围内的待办事项
 * 注意：此函数仅从本地存储读取数据，不调用 Supabase API
 * Supabase 数据通过定时任务自动拉取并更新到本地存储
 */
export async function getTodosInDateRange(
  startDate: Date,
  endDate: Date
): Promise<Todo[]> {
  const todos = await getTodos();
  return getTodosInDateRangeFromList(todos, startDate, endDate);
}

/**
 * 获取指定日期的待办事项
 * 注意：此函数仅从本地存储读取数据，不调用 Supabase API
 * Supabase 数据通过定时任务自动拉取并更新到本地存储
 */
export async function getTodosForDate(date: Date): Promise<Todo[]> {
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);
  
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);
  
  return getTodosInDateRange(startOfDay, endOfDay);
}

/**
 * 获取个人分组待办事项
 */
export async function getPersonalTodos(): Promise<Todo[]> {
  const user = await getCurrentUser();
  if (!user) {
    return [];
  }

  // 获取用户的个人分组ID
  const { groups } = await getUserGroups();
  const personalGroup = groups.find(g => g.isPersonal);
  
  if (!personalGroup) {
    return [];
  }

  const todos = await getTodos();
  return todos.filter(todo => todo.groupId === personalGroup.id);
}

/**
 * 获取分组待办事项
 */
export async function getGroupTodos(groupId: string): Promise<Todo[]> {
  const todos = await getTodos();
  return todos.filter(todo => todo.groupId === groupId);
}

/**
 * 获取分配给我的所有待办事项（包括个人分组和普通分组）
 */
export async function getMyAssignedTodos(): Promise<Todo[]> {
  const user = await getCurrentUser();
  if (!user) {
    return [];
  }
  
  const todos = await getTodos();
  return todos.filter(todo => 
    todo.assignedTo && todo.assignedTo.length > 0 && todo.assignedTo.includes(user.id)
  );
}

/**
 * 获取所有可见的待办事项（个人分组 + 所有分组的任务）
 */
export async function getAllTodos(): Promise<Todo[]> {
  // 返回所有待办事项（RLS策略已经过滤了用户无权访问的）
  return getTodos();
}
